import { useMemo } from "react";
import { useThermalDesign } from "../../../ThermalDesignContext";
import CustomToolTip from "../../../../../../CommonComponents/CustomToolTip";
import { useHandleModalOpen } from "../hooks/ThermalDesignerHooks";
import { THERMAL_FACE_BODY_VEC, THERMAL_FACE_ORDER } from "../../../ThermalDesignContext";
import type { ThermalFaceId } from "../../../ThermalDesignTypes";
import { NodeSpecItem } from "../../../ThermalDesignTypes";

import {
  ToggleButtonActiveIcon,
  ToggleButtonInactiveIcon,
  BracketsJSONIcon,
  AdjustSettingsIcon,
  TickIconWhite,
} from "../../../SatelliteProfileImgAssets/SatelliteProfileImgAssets";

const getExternalFaceIdFromBodyVec = (bodyVec?: number[] | null): ThermalFaceId | null => {
  if (!bodyVec || bodyVec.length !== 3) return null;
  const key = bodyVec.join(",");
  return THERMAL_FACE_ORDER.find((faceId) => THERMAL_FACE_BODY_VEC[faceId].join(",") === key) ?? null;
};

const getNodeTrackerLabel = (item: NodeSpecItem): string => {
  if (item?.values?.node_isInternal) return item.id;
  return getExternalFaceIdFromBodyVec(item?.values?.node_body_vec) ?? item.id;
};

const SingleInputItem = ({ inputObject }: { inputObject: any }) => (
  <div className="infocard-component-properties-div">
    <div className="infocard-component-properties-sub-div">
      <span className="infocard-units-location-span">{inputObject.name}</span>
      <div className="infocard-units-location-sub-div">
        {inputObject.isEnabledFunction && (
          <button onClick={() => inputObject?.isEnabledFunction(!inputObject?.isEnabled)}>
            {inputObject?.isEnabled ? <ToggleButtonActiveIcon /> : <ToggleButtonInactiveIcon />}
          </button>
        )}
        <div className={`infocard-component-togglebtn-div ${inputObject?.isEnabled ? "" : "disabled"}`}>
          <div className="infocard-component-value-div mass-value">
            <span className="infocard-location-value">{inputObject?.orientation?.join(", ")}</span>
            <span className="infocard-location-value disabled">{inputObject?.value}</span>
          </div>
          <div className="infocard-component-units-div">
            <span className="infocard-location-y-axis">{inputObject?.unit}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
);

interface NodeSpecsBoxProps {
  nodeSpecsOptions: NodeSpecItem[];
  onOpen: () => void;
  onOpenJson: () => void;
  onSelectNode: (id: string) => void;
}

const ThermalModelNodeSpecsBox = ({ nodeSpecsOptions, onOpen, onOpenJson, onSelectNode }: NodeSpecsBoxProps) => {
  const allNodesConfigured = nodeSpecsOptions.every((node) => node.status === "complete");

  return (
    <div
      className={`thermal_model_node_specs_infocard ${allNodesConfigured ? "configured" : ""}`}
      onClick={onOpen}
    >
      <div className="thermal_model_node_specs_img_div">
        <img src={require("../../../SatelliteProfileImgAssets/Thermal_model_icon.png")} alt="Node Specs" />
      </div>
      <div className="thermal_model_node_specs_info_div">
        <div className="thermal_model_node_specs_title_div">
          <div className="thermal_model_node_specs_title">
            <TickIconWhite isActive={allNodesConfigured} />
            SURFACE CONFIGURATION
          </div>
          <div className="thermal_model_node_specs_title_icons">
            <CustomToolTip title={"Open Node Specs JSON Modal"} placement="top-start">
              <button
                className="thermal_model_node_specs_title_icon_btn"
                onClick={(e) => { e.stopPropagation(); onOpenJson(); }}
              >
                <BracketsJSONIcon />
              </button>
            </CustomToolTip>
            <CustomToolTip title={"Open Node Specs Modal"} placement="top-start">
              <button
                className="thermal_model_node_specs_title_icon_btn"
                onClick={(e) => { e.stopPropagation(); onOpen(); }}
              >
                <AdjustSettingsIcon />
              </button>
            </CustomToolTip>
          </div>
        </div>
        <div className="thermal_model_node_specs_tracker_div">
          {nodeSpecsOptions.map((item) => (
            <button
              key={item.id}
              onClick={(e) => { e.stopPropagation(); onSelectNode(item.id); }}
              className={`thermal_model_node_specs_tracker_item state ${item.status} ${item.isSelected ? "selected" : ""}`}
            >
              <span className="nodeSpecIdSpan">{getNodeTrackerLabel(item)}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

interface ConductivityBoxProps {
  nodeSpecsOptions: NodeSpecItem[];
  isCompleted: boolean;
  onOpen: () => void;
  onOpenJson: () => void;
}

const ThermalConductivityBox = ({ nodeSpecsOptions, isCompleted, onOpen, onOpenJson }: ConductivityBoxProps) => {
  const allNodesConfigured = nodeSpecsOptions
    .filter((n) => n.status !== "unavailable")
    .every((n) => n.status === "complete");
  const disabledTooltip = "Configure all surface nodes before setting up thermal conductivity";

  return (
    <CustomToolTip title={!allNodesConfigured ? disabledTooltip : ""} placement="top" className="w-100">
      <div
        className={`thermal_conductivity_infocard ${isCompleted ? "completed" : ""} ${!allNodesConfigured ? "disabled" : ""}`}
        onClick={() => { if (allNodesConfigured) onOpen(); }}
      >
        <div className="thermal_conductivity_infocard_inner_div">
          <div className="thermal_conductivity_infocard_heading">
            <TickIconWhite isActive={isCompleted} />
            <span>Thermal Conductivity</span>
          </div>
          <div className="thermal_model_node_specs_title_icons">
            <CustomToolTip title={allNodesConfigured ? "Open Thermal Conductivity JSON Modal" : ""} placement="top-start">
              <span>
                <button
                  className="thermal_model_node_specs_title_icon_btn"
                  disabled={!allNodesConfigured}
                  onClick={(e) => { e.stopPropagation(); onOpenJson(); }}
                >
                  <BracketsJSONIcon />
                </button>
              </span>
            </CustomToolTip>
            <CustomToolTip title={allNodesConfigured ? "Open Conductance Matrix Modal" : ""} placement="top-start">
              <span>
                <button
                  className="thermal_model_node_specs_title_icon_btn"
                  disabled={!allNodesConfigured}
                  onClick={(e) => { e.stopPropagation(); onOpen(); }}
                >
                  <AdjustSettingsIcon />
                </button>
              </span>
            </CustomToolTip>
          </div>
        </div>
      </div>
    </CustomToolTip>
  );
};

interface SurfaceThresholdsBoxProps {
  onOpen: () => void;
}

const ThermalSurfaceThresholdsBox = ({ onOpen }: SurfaceThresholdsBoxProps) => {
  return (
    <div className="thermal_surface_thresholds_infocard" onClick={onOpen}>
      <div className="thermal_surface_thresholds_infocard_inner_div">
        <div className="thermal_surface_thresholds_infocard_heading">
          <TickIconWhite isActive={false} />
          <span>Surface Thresholds</span>
        </div>
        <div className="thermal_model_node_specs_title_icons">
          <CustomToolTip title={"Open Surface Thresholds Modal"} placement="top-start">
            <button
              className="thermal_model_node_specs_title_icon_btn"
              onClick={(e) => { e.stopPropagation(); onOpen(); }}
            >
              <AdjustSettingsIcon />
            </button>
          </CustomToolTip>
        </div>
      </div>
    </div>
  );
};

const EnvironmentalParametersBox = ({ params }: { params: any[] }) => (
  <div className="environmental_parameters_infocard">
    <div className="environmental_parameters_heading">Environmental Parameters</div>
    {params.map((param) => (
      <SingleInputItem key={param.key ?? `${param.name}-${param.unit}`} inputObject={param} />
    ))}
  </div>
);

const ThermalModelInfoCard = () => {
  const { environmentalParametersOptions, nodeSpecsOptions, setNodeSpecsOptions } = useThermalDesign();
  const { handleModalOpen } = useHandleModalOpen();

  const isThermalConductivityCompleted = useMemo(() => {
    const completeNodes = nodeSpecsOptions.filter((n) => n.status === "complete");
    if (completeNodes.length < 4) return false;
    return completeNodes.some((node, rowIdx) =>
      completeNodes.some((_colNode, colIdx) => {
        if (colIdx === rowIdx) return false;
        return (node.values?.node_conductance?.[colIdx] ?? 0) !== 0;
      })
    );
  }, [nodeSpecsOptions]);

  const handleSelectNode = (id: string) => {
    setNodeSpecsOptions((prev) =>
      prev.map((node) => ({ ...node, isSelected: node.id === id }))
    );
    handleModalOpen("isNodeSpecsModalOpen");
  };

  return (
    <>
      <div className="infocard-label-div smaller">
        <span className="infocard-component-label">THERMAL MODEL</span>
      </div>
      <div className="infocard-properties-div">
        <ThermalModelNodeSpecsBox
          nodeSpecsOptions={nodeSpecsOptions}
          onOpen={() => handleModalOpen("isNodeSpecsModalOpen")}
          onOpenJson={() => handleModalOpen("isNodeSpecsJsonModalOpen")}
          onSelectNode={handleSelectNode}
        />
        <ThermalConductivityBox
          nodeSpecsOptions={nodeSpecsOptions}
          isCompleted={isThermalConductivityCompleted}
          onOpen={() => handleModalOpen("isThermalConductivityModalOpen")}
          onOpenJson={() => handleModalOpen("isThermalConductivityJsonModalOpen")}
        />
        <ThermalSurfaceThresholdsBox
          onOpen={() => handleModalOpen("isSurfaceThresholdsModalOpen")}
        />
        <EnvironmentalParametersBox params={environmentalParametersOptions} />
      </div>
    </>
  );
};

export default ThermalModelInfoCard;
